/**
 * Display formatting — deterministic, no Intl dependency (Hermes-safe).
 * Rules from design/İlkeler ve Sözlük: binlik nokta, ondalık virgül; para simgesi
 * sayıdan sonra boşlukla (1.234,56 ₺); negatif için "−" (U+2212); yüzde işareti
 * sayıdan önce (%22,8); tarih dd.MM.yyyy; saat dilimi Europe/Istanbul.
 */
import { D, type DecimalInput } from './money';

export type Locale = 'tr' | 'en';
export const MINUS = '−';

const SEP: Record<Locale, { group: string; decimal: string }> = {
  tr: { group: '.', decimal: ',' },
  en: { group: ',', decimal: '.' },
};

export const CURRENCY_SYMBOLS: Record<string, string> = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£',
  SEK: 'kr',
  PLN: 'zł',
  CAD: 'C$',
  MXN: 'MX$',
  CNY: '¥',
};

export function currencySymbol(code: string): string {
  return CURRENCY_SYMBOLS[code] ?? code;
}

/**
 * The decimal mark of a locale ("," in Turkish). `formatNumber` writes it on its
 * own; a screen that builds a number *while it is being typed* — the M-04 keypad,
 * whose value may end in a bare comma — needs the character itself, and must not
 * keep a second copy of this table.
 */
export function decimalMark(locale: Locale = 'tr'): string {
  return SEP[locale].decimal;
}

export interface NumberFormatOptions {
  /** fixed number of decimals (default 2) */
  decimals?: number;
  /** trim trailing zeros down to this many decimals */
  minDecimals?: number;
  locale?: Locale;
  /** always print a sign (+ / −) */
  signed?: boolean;
  /** group thousands (default true) */
  grouping?: boolean;
}

export function formatNumber(value: DecimalInput, opts: NumberFormatOptions = {}): string {
  const { decimals = 2, minDecimals, locale = 'tr', signed = false, grouping = true } = opts;
  const sep = SEP[locale];
  const d = D(value);
  let fixed = d.abs().toFixed(decimals);
  if (minDecimals !== undefined && minDecimals < decimals && fixed.includes('.')) {
    const [i, f = ''] = fixed.split('.');
    let frac = f;
    while (frac.length > minDecimals && frac.endsWith('0')) frac = frac.slice(0, -1);
    fixed = frac.length ? `${i}.${frac}` : i!;
  }
  const [intPart, fracPart] = fixed.split('.');
  const grouped = grouping ? intPart!.replace(/\B(?=(\d{3})+(?!\d))/g, sep.group) : intPart!;
  const body = fracPart !== undefined ? `${grouped}${sep.decimal}${fracPart}` : grouped;
  const isZero = D(fixed).isZero();
  if (d.isNegative() && !isZero) return `${MINUS}${body}`;
  if (signed && !isZero) return `+${body}`;
  return body;
}

export interface MoneyFormatOptions extends NumberFormatOptions {
  /** hide the currency symbol */
  bare?: boolean;
}

/** 1.234,56 ₺ · −38.420,00 $ */
export function formatMoney(value: DecimalInput, currency: string, opts: MoneyFormatOptions = {}): string {
  const n = formatNumber(value, { decimals: 2, ...opts });
  return opts.bare ? n : `${n} ${currencySymbol(currency)}`;
}

/** Compact money for KPI tiles: 4,82 M ₺ · 684,2 B ₺ */
export function formatMoneyCompact(value: DecimalInput, currency: string, locale: Locale = 'tr'): string {
  const d = D(value);
  const abs = d.abs();
  const units = locale === 'tr' ? { m: 'M', k: 'B' } : { m: 'M', k: 'K' };
  let body: string;
  if (abs.greaterThanOrEqualTo(1_000_000)) body = `${formatNumber(abs.dividedBy(1_000_000), { decimals: 2, locale })} ${units.m}`;
  else if (abs.greaterThanOrEqualTo(10_000)) body = `${formatNumber(abs.dividedBy(1000), { decimals: 1, locale })} ${units.k}`;
  else body = formatNumber(abs, { decimals: 0, locale });
  return `${d.isNegative() && !abs.isZero() ? MINUS : ''}${body} ${currencySymbol(currency)}`;
}

/** %22,8 · −%4,0 · +%1,7 (signed) */
export function formatPercent(value: DecimalInput, opts: { decimals?: number; signed?: boolean; locale?: Locale } = {}): string {
  const { decimals = 1, signed = false, locale = 'tr' } = opts;
  const d = D(value);
  const body = formatNumber(d.abs(), { decimals, locale });
  const zero = D(d.abs().toFixed(decimals)).isZero();
  const sign = d.isNegative() && !zero ? MINUS : signed && !zero ? '+' : '';
  return locale === 'tr' ? `${sign}%${body}` : `${sign}${body}%`;
}

/** Quantity with unit precision: 288,5 kg · 1.470 koli · 12,5 koli */
export function formatQty(value: DecimalInput, unitLabel: string, precision = 0, locale: Locale = 'tr'): string {
  return `${formatNumber(value, { decimals: precision, minDecimals: 0, locale })} ${unitLabel}`.trim();
}

// ── dates ────────────────────────────────────────────────────────────────────
// Turkey has used UTC+3 year-round since 2016. A fixed offset keeps formatting
// deterministic across web and Hermes; revisit if DST is ever reintroduced.
export const ISTANBUL_OFFSET_MIN = 180;

const MONTHS: Record<Locale, { long: string[]; short: string[] }> = {
  tr: {
    long: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
    short: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
  },
  en: {
    long: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    short: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  },
};
const WEEKDAYS: Record<Locale, string[]> = {
  // index 0 = Monday (week starts Monday)
  tr: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
};

export type DateInput = Date | string | number;

export interface LocalParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  weekday: number; // 0 = Monday
}

/** Parse "2026-09-22" as a calendar date (no timezone shift) or a timestamp. */
export function toDate(input: DateInput): Date {
  if (input instanceof Date) return input;
  if (typeof input === 'number') return new Date(input);
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map(Number) as [number, number, number];
    // noon Istanbul time → the same calendar day in any view
    return new Date(Date.UTC(y, m - 1, d, 12 - ISTANBUL_OFFSET_MIN / 60));
  }
  return new Date(input);
}

export function istanbulParts(input: DateInput): LocalParts {
  const t = toDate(input).getTime() + ISTANBUL_OFFSET_MIN * 60_000;
  const d = new Date(t);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    weekday: (d.getUTCDay() + 6) % 7,
  };
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** 22.09.2026 */
export function formatDate(input: DateInput): string {
  const p = istanbulParts(input);
  return `${pad(p.day)}.${pad(p.month)}.${p.year}`;
}

/** 2026-09-22 (Istanbul calendar day) — for DB date columns */
export function toIsoDate(input: DateInput): string {
  const p = istanbulParts(input);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function todayIso(now: DateInput = new Date()): string {
  return toIsoDate(now);
}

/** 20 Eyl · 20 Eyl 2025 (other year) */
export function formatDateShort(input: DateInput, locale: Locale = 'tr', now: DateInput = new Date()): string {
  const p = istanbulParts(input);
  const n = istanbulParts(now);
  const base = `${pad(p.day)} ${MONTHS[locale].short[p.month - 1]}`;
  return p.year === n.year ? base : `${base} ${p.year}`;
}

/** 21 Tem 2026 · 04 Mar 2026 — the year is always written (BOM versions, cost cards, documents). */
export function formatDateMedium(input: DateInput, locale: Locale = 'tr'): string {
  const p = istanbulParts(input);
  return `${pad(p.day)} ${MONTHS[locale].short[p.month - 1]} ${p.year}`;
}

/** "2026-09" → "Eyl" — the 12-month trend axis of W-05 › Maliyet. */
export function monthShortLabel(isoMonth: string, locale: Locale = 'tr'): string {
  const m = Number(isoMonth.slice(5, 7));
  return MONTHS[locale].short[m - 1] ?? '';
}

/** 22 Eylül 2026 */
export function formatDateLong(input: DateInput, locale: Locale = 'tr'): string {
  const p = istanbulParts(input);
  return `${p.day} ${MONTHS[locale].long[p.month - 1]} ${p.year}`;
}

/** Pazartesi, 21 Eylül 2026 */
export function formatDateWithWeekday(input: DateInput, locale: Locale = 'tr'): string {
  const p = istanbulParts(input);
  return `${WEEKDAYS[locale][p.weekday]}, ${formatDateLong(input, locale)}`;
}

export function formatTime(input: DateInput): string {
  const p = istanbulParts(input);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Today → "14:05"; otherwise "20 Eyl 17:20" (design rule for timestamps). */
export function formatTimestamp(input: DateInput, locale: Locale = 'tr', now: DateInput = new Date()): string {
  const p = istanbulParts(input);
  const n = istanbulParts(now);
  if (p.year === n.year && p.month === n.month && p.day === n.day) return formatTime(input);
  return `${formatDateShort(input, locale, now)} ${formatTime(input)}`;
}

/** "şimdi", "3 dk önce", "2 sa önce", "dün", "4 gün önce" */
export function formatRelative(input: DateInput, locale: Locale = 'tr', now: DateInput = new Date()): string {
  const diffMin = Math.floor((toDate(now).getTime() - toDate(input).getTime()) / 60_000);
  const tr = locale === 'tr';
  if (diffMin < 1) return tr ? 'şimdi' : 'just now';
  if (diffMin < 60) return tr ? `${diffMin} dk önce` : `${diffMin} min ago`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return tr ? `${h} sa önce` : `${h} h ago`;
  const days = daysBetween(input, now);
  if (days === 1) return tr ? 'dün' : 'yesterday';
  return tr ? `${days} gün önce` : `${days} days ago`;
}

/** Whole Istanbul calendar days from a to b (b − a). */
export function daysBetween(a: DateInput, b: DateInput): number {
  const pa = istanbulParts(a);
  const pb = istanbulParts(b);
  const ua = Date.UTC(pa.year, pa.month - 1, pa.day);
  const ub = Date.UTC(pb.year, pb.month - 1, pb.day);
  return Math.round((ub - ua) / 86_400_000);
}

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export function monthLabel(isoMonth: string, locale: Locale = 'tr'): string {
  const [y, m] = isoMonth.split('-').map(Number) as [number, number];
  return `${MONTHS[locale].long[m - 1]} ${y}`;
}

export function monthStart(input: DateInput = new Date()): string {
  const p = istanbulParts(input);
  return `${p.year}-${pad(p.month)}-01`;
}
